import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import usePageMeta from "@/hooks/usePageMeta";

const faqs = [
  {
    group: "关于内容",
    items: [
      {
        q: "本站可以观看影片吗？",
        a: "不可以。本站是影视资料与资讯展示平台，只提供影片的条目信息、剧情简介与创作背景，不提供任何在线播放、下载链接或资源分发服务。",
      },
      {
        q: "站内的影片是真实存在的吗？",
        a: "不是。站内全部影片、导演、演员与资讯均为演示用的虚构内容，用于展示网站的信息结构与浏览体验，若与现实作品雷同纯属巧合。",
      },
      {
        q: "内容适合什么年龄的用户？",
        a: "本站面向 18 岁以上的成年用户。如果你未满法定年龄，请立即离开本站。",
      },
      {
        q: "多久更新一次片库与资讯？",
        a: "演示版本的片库固定为 20 部作品、8 篇资讯。正式运营时的更新频率会在首页资讯板块公布。",
      },
    ],
  },
  {
    group: "浏览与筛选",
    items: [
      {
        q: "如何按地区、年代和题材筛选影片？",
        a: "进入任意分类页后，左侧（手机端点击「筛选」按钮展开）提供地区、年份区间、片长与主题标签四组条件，可组合使用，结果会即时刷新。",
      },
      {
        q: "为什么筛选后显示「暂无符合条件的影片」？",
        a: "说明当前分类下没有同时满足所有条件的作品。点击重置按钮即可清空筛选条件，回到该分类的完整列表。",
      },
      {
        q: "排序方式有哪些？",
        a: "分类页右上角支持四种排序：最新发布（默认）、年份从新到旧、评分最高、标题排序。切换后列表与分页会同步更新。",
      },
      {
        q: "搜索支持哪些关键词？",
        a: "可以搜索影片名称、英文原名、导演、演员、地区（日本 / 韩国）以及题材标签（如文艺、悬疑、短片）。",
      },
    ],
  },
  {
    group: "功能与账号",
    items: [
      {
        q: "收藏和分享按钮会保存数据吗？",
        a: "不会。收藏与分享仅为界面演示：收藏只改变当前页面的按钮状态，刷新后恢复；分享会调用浏览器的系统分享或复制链接。本站没有账号体系。",
      },
      {
        q: "可以把影片链接发给朋友吗？",
        a: "可以。每部影片都有独立的详情页地址（/detail/影片标识），复制浏览器地址栏链接即可直接分享。",
      },
      {
        q: "页面在手机上显示不完整怎么办？",
        a: "请确认浏览器没有开启强制缩放。站点已适配手机端：导航收为汉堡菜单，筛选面板折叠，卡片自动切换为单列或双列布局。",
      },
    ],
  },
  {
    group: "版权与联系",
    items: [
      {
        q: "站内图片与文字可以转载吗？",
        a: "站内配图与文字均为演示素材。如需引用，请先通过关于页的邮箱与我们联系并注明出处。",
      },
      {
        q: "发现信息错误如何反馈？",
        a: "可以通过关于页的联系邮箱反馈具体页面与错误内容，我们会核实后更新条目。",
      },
    ],
  },
];

export default function FaqPage() {
  usePageMeta(
    "常见问题 - 日韩黄色电影平台",
    "关于日韩黄色电影平台的常见问题：内容范围、筛选与搜索方式、收藏分享功能说明以及版权与联系方式。",
    "日韩黄色电影,常见问题,FAQ,日韩情色电影,使用说明",
  );

  const [openKeys, setOpenKeys] = useState<string[]>(["关于内容-0"]);

  const toggle = (key: string) => {
    setOpenKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key],
    );
  };

  return (
    <SiteLayout>
      <div className="w-full bg-background-50">
        <section className="w-full px-4 md:px-6 pt-10 md:pt-16 pb-10 md:pb-12 border-b border-background-300/60">
          <div className="w-full max-w-[1200px] mx-auto">
            <nav className="flex items-center gap-1.5 text-[11px] md:text-xs text-foreground-500">
              <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-accent-500">常见问题</span>
            </nav>

            <div className="mt-6 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
              <div className="min-w-0">
                <p className="font-label text-[11px] tracking-[0.26em] uppercase text-accent-500">
                  FAQ
                </p>
                <h1 className="mt-3 font-heading text-3xl md:text-5xl text-foreground-950">
                  常见问题
                </h1>
                <p className="mt-4 max-w-[620px] text-xs md:text-sm leading-relaxed text-foreground-500">
                  关于内容范围、筛选方式与功能演示说明的集中解答。点击问题即可展开答案，
                  没找到想问的，可以通过关于页的邮箱联系我们。
                </p>
              </div>

              <div className="flex items-center gap-3 rounded-lg border border-background-300/60 bg-background-100 px-4 py-3 shrink-0">
                <span className="w-9 h-9 rounded-md bg-accent-500/10 text-accent-500 flex items-center justify-center">
                  <i className="ri-question-line" />
                </span>
                <div>
                  <p className="text-xs text-foreground-800">共 {faqs.reduce((sum, group) => sum + group.items.length, 0)} 个问题</p>
                  <p className="mt-0.5 text-[10px] text-foreground-400">4 个分类</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-16">
          <div className="w-full max-w-[900px] mx-auto flex flex-col gap-8 md:gap-10">
            {faqs.map((group) => (
              <div key={group.group}>
                <h2 className="font-heading text-lg md:text-xl text-foreground-950 flex items-center gap-2">
                  <span className="w-1 h-4 rounded-full bg-primary-500" />
                  {group.group}
                </h2>

                <div className="mt-4 rounded-lg border border-background-300/60 bg-background-100 overflow-hidden">
                  {group.items.map((item, index) => {
                    const key = `${group.group}-${index}`;
                    const isOpen = openKeys.includes(key);
                    return (
                      <div key={key} className="border-b border-background-300/50 last:border-b-0">
                        <button
                          type="button"
                          onClick={() => toggle(key)}
                          aria-expanded={isOpen}
                          className="w-full flex items-center justify-between gap-4 px-4 md:px-5 py-4 text-left cursor-pointer hover:bg-background-200/40 transition-colors"
                        >
                          <span className="flex items-start gap-3 min-w-0">
                            <span className="font-label text-[11px] text-accent-500 mt-1 shrink-0">
                              {String(index + 1).padStart(2, "0")}
                            </span>
                            <span
                              className={`text-sm md:text-[15px] leading-snug transition-colors ${
                                isOpen ? "text-accent-500" : "text-foreground-800"
                              }`}
                            >
                              {item.q}
                            </span>
                          </span>
                          <i
                            className={`shrink-0 text-lg transition-transform duration-300 ${
                              isOpen ? "ri-subtract-line text-accent-500" : "ri-add-line text-foreground-400"
                            }`}
                          />
                        </button>
                        {isOpen && (
                          <div className="px-4 md:px-5 pb-5 -mt-1 fade-in">
                            <p className="pl-7 text-xs md:text-sm leading-[1.9] text-foreground-500">
                              {item.a}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="rounded-lg bg-primary-500 px-5 md:px-7 py-6 md:py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="min-w-0">
                <h2 className="font-heading text-lg md:text-xl text-foreground-950">
                  还有其他疑问？
                </h2>
                <p className="mt-1.5 text-xs md:text-sm text-foreground-950/80 leading-relaxed">
                  通过邮件说明你的问题，我们会尽量在三个工作日内回复。
                </p>
              </div>
              <a
                href="mailto:contact@jkcinema-demo.com"
                className="h-10 px-5 rounded-md bg-background-50 text-foreground-900 text-xs flex items-center justify-center gap-2 cursor-pointer hover:bg-background-100 transition-colors whitespace-nowrap shrink-0"
              >
                <i className="ri-mail-line" />
                发送邮件
              </a>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}